import React, { useState } from 'react';
import { X, Copy, Check, Share } from 'lucide-react';
import { Button } from '../UI/Button';
import { Card } from '../UI/Card';

interface ReceiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  walletAddress: string;
}

export const ReceiveModal: React.FC<ReceiveModalProps> = ({ isOpen, onClose, walletAddress }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = walletAddress;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Algorand Wallet Address',
          text: `Send ALGO to this address: ${walletAddress}`,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      handleCopy();
    }
  };

  // Generate QR code URL (using a free QR code service)
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(walletAddress)}&bgcolor=FFFFFF&color=000000&margin=10`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Receive ALGO</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        <div className="text-center space-y-4">
          <div className="bg-white border-2 border-gray-200 rounded-lg p-4 inline-block">
            <img
              src={qrCodeUrl}
              alt="Wallet QR Code"
              className="w-48 h-48 mx-auto"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = document.createElement('div');
                fallback.className = 'w-48 h-48 bg-gray-100 flex items-center justify-center text-gray-500 text-sm';
                fallback.textContent = 'QR Code unavailable';
                target.parentNode?.appendChild(fallback);
              }}
            />
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-2">Your Algorand Address</p>
            <div className="bg-gray-50 p-3 rounded-lg border">
              <p className="text-sm font-mono text-gray-900 break-all leading-relaxed">
                {walletAddress}
              </p>
            </div>
          </div>

          <div className="flex space-x-2">
            <Button
              onClick={handleCopy}
              variant="outline"
              fullWidth
              className="flex items-center justify-center"
            >
              {copied ? (
                <>
                  <Check size={16} className="mr-2 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={16} className="mr-2" />
                  Copy Address
                </>
              )}
            </Button>
            
            <Button
              onClick={handleShare}
              variant="outline"
              fullWidth
              className="flex items-center justify-center"
            >
              <Share size={16} className="mr-2" />
              Share
            </Button>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Important:</strong> Only send ALGO and ASA (Algorand Standard Assets) tokens to this address. 
              Sending other cryptocurrencies may result in permanent loss of funds.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};